import applyClassNames from "classnames";

import { CountryChipItem } from "@similarweb/ui-components";

import { getCountries } from "app/components/filters-bar/utils";
import HooksModalV2Key from "app/constants/HooksModalV2Key";
import { translate } from "app/filters/i18nFilter";
import { CountrySelectCombobox } from "app/pages/sales-intelligence/common-components/dropdown/CountrySelectCombobox";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import {
    StyledChipItemsContainer,
    StyledChipSeparator,
    StyledDDContainer,
    StyledSelectionWrapper,
} from "app/pages/sales-intelligence/modules/leads-search-filters/components/filters/VisitsFromFilter/components/CountriesDDWithChips/styles";
import { useInjectedVisitsFromSingleCountry } from "app/pages/sales-intelligence/modules/leads-search-filters/context/VisitsFromSingleCountryContext";
import settingsService from "app/pages/sales-intelligence/sub-modules/advanced-search/services/sw-settings/settingsService";
import type { ICountryObject } from "app/services/CountryService";
import { openHooksModalV5 } from "app/services/ModalService";

type CountriesDDWithChipsProps = {
    isWrapperHidden: boolean;
    countries: readonly ICountryObject[];
    selectedIds: readonly ICountryObject["id"][];
    onSelect(countriesIds: number[]): void;
};

const CountriesDDWithChips = (props: CountriesDDWithChipsProps) => {
    const { countries, selectedIds, isWrapperHidden, onSelect } = props;
    const isSingleCountryMode = useInjectedVisitsFromSingleCountry() ?? false;
    const restrictedCountryAction = (): void => {
        openHooksModalV5(HooksModalV2Key.WebCountry);
    };

    const allCountries = settingsService.getCountriesWithoutWW(getCountries());
    // The first non-permitted country opens the divider separating permitted from restricted rows.
    const firstRestrictedId = allCountries.find((country) => !country.permitted)?.id;

    const chipItems = selectedIds.reduce<
        {
            id: number;
            text: string;
            icon: string;
            permitted?: boolean;
            onCloseItem(): void;
        }[]
    >((items, id) => {
        const countryObject = countries.find((c) => c.id === id);

        if (typeof countryObject !== "undefined") {
            items.push({
                text: countryObject.text,
                id: countryObject.id,
                icon: countryObject.icon as string,
                permitted: countryObject.permitted,
                onCloseItem() {
                    handleSelection(countryObject);
                },
            });
        }

        return items;
    }, []);

    function handleSelection(country: ICountryObject) {
        if (isSingleCountryMode) {
            return onSelect([country.id]);
        }

        if (selectedIds.includes(country.id)) {
            return onSelect(selectedIds.filter((id) => id !== country.id));
        }

        onSelect(selectedIds.concat(country.id));
    }

    function renderChipItemsSeparator(index: number) {
        return (
            <I18n
                key={`separator-${index}`}
                element={StyledChipSeparator}
                data-automation-index={index}
                data-automation="chip-item-separator"
                translatable={{ key: "si.common.or" }}
            />
        );
    }

    return (
        <StyledSelectionWrapper
            className={applyClassNames({
                hidden: isWrapperHidden,
            })}
            hasBorder={chipItems.length > 0}
            hasPadding={chipItems.length > 0}
        >
            <StyledChipItemsContainer
                items={chipItems}
                // @ts-ignore
                ChipComponent={CountryChipItem}
                renderSeparator={renderChipItemsSeparator}
            />
            <StyledDDContainer hasMargin={chipItems.length > 0}>
                <CountrySelectCombobox
                    multi
                    isTopLayer
                    items={allCountries}
                    onSelect={handleSelection}
                    onRestrictedClick={() => {
                        restrictedCountryAction();
                        return true;
                    }}
                    renderSeparatorBefore={(country) => country.id === firstRestrictedId}
                    selectedIds={selectedIds}
                    buttonText={translate("si.components.countries_dd.button.text")}
                    searchPlaceHolder={translate("si.components.countries_selection_modal.dd_placeholder")}
                />
            </StyledDDContainer>
        </StyledSelectionWrapper>
    );
};

export default CountriesDDWithChips;
